"use client";

import { useEffect, useRef, useState } from "react";
import moment from 'moment';
import Chart from "./Chart";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SquareMenu } from "lucide-react";
import { ISCHART_INFORMATICS_GS } from "@/informatics/config";
import Selector from "./Selector";

export default function ChartPreviewContent(props: any) {
  const [trigger, setTrigger] = useState(false);
  const [previewItems, setPreviewItems] = useState<any[]>([]);
  const [displayItems, setDisplayItems] = useState<any>(null);
  const [search, setSearch] = useState("");
  const imageRef = useRef<any>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const imageUrl = useRef<any>({});
  const initialLoad = useRef(false);
  const [openSelector, setOpenSelector] = useState(false);
  const [openAdmin, setOpenAdmin] = useState(false);
  const [pane, setPane] = useState(0);

  let {datasetId, selectedTab, showChart, selectedDivision} = props;

  let displayPanes = datasetId ? ["Period", "Period Breakdown"].concat(selectedTab === "Recent" ? [] : ["Dimensions"]) : [];

  let Admin = props.Admin

  useEffect(() => {
    if (props.datasetId
        && props.chartInfo
        && props.chartInfo?.[props.datasetId]?.previewItems
        && props.chartInfo?.[props.datasetId]?.displayItems
    ) {
      let divisionId = getPrefix(props.datasetId) === "" ? null
          : selectedDivision?.[props.datasetId];
      if (divisionId
          && props.chartInfo[props.datasetId]?.divisionItems?.[divisionId]?.previewItems
          && props.chartInfo[props.datasetId]?.divisionItems?.[divisionId]?.displayItems) {
        setPreviewItems(props.chartInfo[props.datasetId].divisionItems[divisionId].previewItems);
        setDisplayItems(props.chartInfo[props.datasetId].divisionItems[divisionId].displayItems);
      } else {
        setPreviewItems(props.chartInfo[props.datasetId].previewItems);
        setDisplayItems(props.chartInfo[props.datasetId].displayItems);
      }
    }
  }, [props.chartInfo, props.datasetId, selectedDivision]);

  useEffect(() => {
    updateImage();
  }, [displayItems, pane, search]);

  useEffect(() => {
    setPane(0);
    updateImage();
  }, [props.selectedTab]);

  const isVisible = (image: HTMLImageElement, container: HTMLDivElement) => {
    let { top, bottom, left, right } = container.getBoundingClientRect();
    let imageRect = image?.getBoundingClientRect();
    if (!imageRect) return false;
    let threshold = 20;
    return (
      (imageRect.top > top && imageRect.top < bottom)
      || (imageRect.bottom > top && imageRect.bottom < bottom)
      || (imageRect.left > left && imageRect.left < right)
      || (imageRect.right > left && imageRect.right < right)
      || (Math.abs(imageRect.top - bottom) < threshold)
      || (Math.abs(imageRect.top - top) < threshold)
      || (Math.abs(imageRect.bottom - top) < threshold)
      || (Math.abs(imageRect.bottom - bottom) < threshold)
      || (Math.abs(imageRect.left - right) < threshold)
      || (Math.abs(imageRect.left - left) < threshold)
      || (Math.abs(imageRect.right - left) < threshold)
      || (Math.abs(imageRect.right - right) < threshold)
    );
  }

  const getPrefix = (datasetId: string) => {
    const divisionId = selectedDivision?.[datasetId];
    if (!divisionId || divisionId === "All" ) {
      return ""
    } else {
      return `/${divisionId}`
    }
  }

  const toUrl = (clickItem: string) =>
    props.useCloud
    ? (`${ISCHART_INFORMATICS_GS}/${props.datasetId}`
       + getPrefix(props.datasetId) + `/thumbnails/${clickItem}.png`)
    : (`${props.urlPrefix}data/${props.datasetId}`
       + getPrefix(props.datasetId) + `/thumbnails/${clickItem}.png`)

  const updateImage = () => {
    let urlList = [];
    for (var i in imageRef.current) {
      if (isVisible(imageRef.current?.[i], containerRef.current!)
          && !(Object.keys(imageUrl.current).includes(toUrl(previewItems?.[Number(i)]?.filename)))
      ) {
        urlList.push(toUrl(previewItems?.[Number(i)]?.filename));
      }
    }
    if (urlList.length > 0) {
      getDownloadUrlList(urlList);
    } else {
      setTrigger(!trigger);
    }
  }

  const getDownloadUrlList = async (urlList: string[]) => {
    let downloadUrlList = await Promise.all(
      urlList
      .map(async (originalUrl: string) => {
        imageUrl.current[originalUrl] = "";
        if (props.useCloud) {
          return await props.toDownloadUrl(originalUrl);
        }
        return originalUrl;
      })
    );
    for (var i = 0; i < urlList.length; i++) {
      imageUrl.current[urlList[i]] = downloadUrlList[i];
    }
    setTrigger(!trigger);
  }

  const selectedDisplayItem = displayItems?.[selectedTab];

  return (
  <>
    <div>
      <div style={{height: "3vh", display: "flex", borderBottom: "solid grey 1px", position: "relative"}}>
        <div style={{display: "flex", alignItems: "center", width: "100%", marginRight: "10px", justifyContent: "space-between"}}>
          {props.mobile &&
          <>
            <SquareMenu
              onClick={(e: any) => setOpenSelector(!openSelector)}
            />
            {openSelector &&
            <div style={{position: "absolute", left: 0, top: "2rem", zIndex: 1000}}>
              <Selector
                chartInfo={props.chartInfo}
                selectedDatasetId={props.datasetId}
                onSelectDataset={(datasetId: string) => {
                  props.setState({ datasetId: datasetId });
                  setOpenSelector(false);
                }}
              />
            </div>}
          </>}
          {search &&
          <div onClick={(e: any) => setSearch("")} style={{padding: "0 5px 0 5px"}}>x</div>}
          <input
            placeholder="search"
            style={{width: "300px", border: "solid grey 1px", flex: 1}}
            value={search}
            onChange={(e: any) => {
              setSearch(e.target.value);
            }}
          />
          {search &&
          <div
            style={{
              position: "absolute", top: 30, right: 2,
              maxHeight: "500px", padding: "5px", border: "solid grey 1px",
              backgroundColor: "lightyellow", overflowY: "scroll"
            }}>
            {(previewItems || [])
            .map((item: any, index: number) => ({
                originalIndex: index,
                parts: item.filename.split("."),
                agg: item.filename.split(".")?.[1],
                dimCount: item.filename.split(".")?.[0].split("__").length,
                ...item
              }))
            .filter((item: any) => (
              (item.filter?.y && item.filter.y === moment().format('YYYY')) ||
              (item.filter?.ym && item.filter.ym === moment().format('YYYY-MM'))))
            .filter((item: any) => (
              search ? item.filename.toLowerCase().includes(search.toLowerCase())
              : true
            ))
            .map((item: any, index: number) => (
              <div onClick={(e: any) => setSearch(item.filename)}>
                {item.filename.split(".")?.[2]}
              </div>
            ))}
          </div>}
          {datasetId &&
          <div className="flex">
            {Admin &&
            <div
              className="border border-black mr-1 px-4"
              style={{cursor: "pointer"}}
              onClick={(e: any) => setOpenAdmin(true)}>
              Admin
            </div>}
            <div
              className="border border-black mr-1 px-4"
              style={{cursor: "pointer"}}
              onClick={(e: any) => updateImage()}>
              Refresh
            </div>
          </div>}
        </div>
      </div>

      {/* Division Selector and Tabs Section */}
      {datasetId && props.chartInfo?.[datasetId] && (
        <div style={{ padding: "5px", borderBottom: "solid grey 1px", backgroundColor: "#f5f5f5" }}>
          {/* Division Selector */}
          {props.allDivisionRead && props.chartInfo[datasetId]?.divisionItems && (
            <div style={{ marginBottom: "5px" }}>
              <select
                value={selectedDivision?.[datasetId] || "All"}
                onChange={(e: any) => {
                  console.log('onChange datasetId: ', datasetId);
                  console.log('onChange e.target.value: ', e.target.value);
                  console.log('onChange selectedDivision: ', selectedDivision);
                  props.setSelectedDivision({
                    ...selectedDivision,
                    [datasetId]: e.target.value
                  });
                }}
                className="bg-blue-100 px-2 py-1 rounded border border-gray-300"
                style={{ width: "100%" }}
              >
                {[{ division_id: "All", division_name: "All" }]
                  .concat(Object.values(props.chartInfo[datasetId]?.divisionItems) || [])
                  .map((division: any, index: number) => (
                    <option key={index} value={division?.division_id}>
                      {division?.division_name}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* Tabs List */}
          <div>
            {Object.keys(props.chartInfo[datasetId]?.displayItems || {})
              .map((tab: string, tabIndex: number) => {
                return (
                  <div
                    style={{
                      paddingLeft: "10px",
                      padding: "5px",
                      cursor: "pointer",
                      borderBottom: "solid grey 1px",
                      backgroundColor: selectedTab === tab ? "lightpink" : "white"
                    }}
                    className="whitespace-normal break-words"
                    key={tabIndex}
                    onClick={(e: any) => {
                      props.setState({ selectedTab: tab, datasetId: datasetId });
                    }}
                  >
                    {tab}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      <div
        ref={containerRef}
        style={{
          height: "91vh",
          border: "solid black 1px",
          display: "flex",
          flexDirection: "column"
        }}
        onScroll={updateImage}>
        {displayPanes
          .map((label: string, original: number) => ({original, label}))
          .filter((label: {original: number, label: string}, index: number) => index <= pane)
          .map((item: {original: number, label: string}, index: number) => {
            return (
              <div
                className={"cursor-pointer border border-gray-300 " + (pane === item.original ? "bg-green-200" : "bg-gray-200")}
                key={index}
                onClick={(e: any) => setPane(item.original)}>
                {item.label}
              </div>
            )
          })
        }
        {selectedDisplayItem ?
        <div style={{ padding: "5px", flex: 1, overflowY: "scroll", }} onScroll={updateImage}>
          {pane === 0 &&
          <>
            <div style={{display: "flex", overflowX: "scroll"}}>
              {Object.entries(selectedDisplayItem?.onefilter || {})
              .sort((a: any, b: any) => a[0] < b[0] ? 1 : -1)
              .map((entry: any, entryIndex: number) => ([
                entry[0],
                entry[1]
                .filter((item: any) => Object.keys(item?.filter)?.length == 1)
                .filter((item: any) =>(
                  search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                  : true
                ))]
              ))
              .filter((entry: any) => entry[1].length > 0)
              .map((entry: any, entryIndex: number) => { return (
                <div key={entryIndex}>
                  <div>{entry[0]}</div>
                  <div style={{display: "flex", overflowX: "scroll"}} onScroll={updateImage}>
                    {entry[1]
                    .sort((a: any, b: any) =>
                      a.filename.split(".")?.[2] < b.filename.split(".")?.[2] ? -1 : 1)
                    .map((item: any, itemIndex: number) => (
                      <PreviewPic
                        key={itemIndex}
                        setState={props.setState}
                        selectedTab={selectedTab}
                        datasetId={props.datasetId}
                        previewItem={item}
                        initialLoad={initialLoad}
                        updateImage={updateImage}
                        imageUrl={imageUrl}
                        imageRef={imageRef}
                        index={item.originalIndex}
                        toUrl={toUrl}
                        urlPrefix={props.urlPrefix}
                      />
                    ))}
                  </div>
                </div>
              )})}
            </div>
            <div style={{display: "flex", overflowX: "scroll"}}>
              {Object.entries(selectedDisplayItem?.twofilter || {})
              .sort((a: any, b: any) => a[0] < b[0] ? 1 : -1)
              .map((entry: any, entryIndex: number) => ([
                entry[0],
                entry[1]
                .filter((item: any) => (Object.keys(item?.filter)?.length === 1
                                       && ["y", "ym", "ymd"].includes(item?.parts?.[0]?.split("__")?.[0])))
                .filter((item: any) =>(
                  search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                  : true))
              ]))
              .filter((entry: any) => entry[1].length > 0)
              .map((entry: any, entryIndex: number) => { return (
                <div key={entryIndex}>
                  <div>{entry[0]}</div>
                  <div style={{display: "flex", overflowX: "scroll"}} onScroll={updateImage}>
                    {entry[1]
                    .sort((a: any, b: any) => {
                      return (
                        a.filename.split(".")?.[0]?.split("__")?.[1]
                        < b.filename.split(".")?.[0]?.split("__")?.[1] ? -1 : 1
                      )
                    })
                    .map((item: any, itemIndex: number) => { return(
                      <PreviewPic
                        key={itemIndex}
                        setState={props.setState}
                        selectedTab={selectedTab}
                        datasetId={props.datasetId}
                        previewItem={item}
                        initialLoad={initialLoad}
                        updateImage={updateImage}
                        imageUrl={imageUrl}
                        imageRef={imageRef}
                        index={item.originalIndex}
                        toUrl={toUrl}
                        urlPrefix={props.urlPrefix}
                      />
                    )})}
                  </div>
                </div>
              )})}
            </div>
            <div style={{display: "flex", flexWrap: "wrap", marginTop: "10px"}}>
              {(selectedDisplayItem?.one || [])
              .filter((item: any) => ["y", "ym", "ymd"].includes(item?.parts?.[0]?.split('__')?.[0]))
              .filter((item: any) => (
                search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                : true
              ))
              .map((item: any, childIndex: number) => (
                <PreviewPic
                  key={childIndex}
                  setState={props.setState}
                  selectedTab={selectedTab}
                  datasetId={props.datasetId}
                  previewItem={item}
                  initialLoad={initialLoad}
                  updateImage={updateImage}
                  imageUrl={imageUrl}
                  imageRef={imageRef}
                  index={item.originalIndex}
                  toUrl={toUrl}
                  urlPrefix={props.urlPrefix}
                />
              ))}
            </div>
            <div style={{display: "flex", flexWrap: "wrap", marginTop: "10px"}}>
              {(selectedDisplayItem.two || [])
              .filter((item: any) => ["y", "ym", "ymd"].includes(item?.parts?.[0]?.split("__")?.[0]))
              .filter((item: any) => (
                search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                : true
              ))
              .map((item: any, childIndex: number) => (
                <PreviewPic
                  key={childIndex}
                  setState={props.setState}
                  selectedTab={selectedTab}
                  datasetId={props.datasetId}
                  previewItem={item}
                  initialLoad={initialLoad}
                  updateImage={updateImage}
                  imageUrl={imageUrl}
                  imageRef={imageRef}
                  index={item.originalIndex}
                  toUrl={toUrl}
                  urlPrefix={props.urlPrefix}
                />
              ))}
            </div>
          </>}
          {pane === 1 &&  //----------------------------Period Brekadown pane -------------------
          <>
            <div>
              {Object.entries(selectedDisplayItem?.onefilter || {})
              .sort((a: any, b: any) => a[0] < b[0] ? 1 : -1)
              .map((entry: any, entryIndex: number) => ([
                entry[0],
                entry[1]
                .filter((item: any) => Object.keys(item?.filter)?.length > 1)
                .filter((item: any) =>(
                  search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                  : true
                ))]
              ))
              .filter((entry: any) => entry[1].length > 0)
              .sort((a: any, b: any) => {
                let aFilter: any = a[0].split(" by ");
                let bFilter: any = b[0].split(" by ");
                if (aFilter[0] < bFilter[0])
                  return 1
                else if (aFilter[0] > bFilter[0])
                  return -1
                else if (aFilter[1] < bFilter[1])
                  return -1
                else
                  return  1
              })
              .map((entry: any, entryIndex: number) => { return (
                <div key={entryIndex}>
                  <div>{entry[0]}</div>
                  <div style={{display: "flex", overflowX: "scroll"}} onScroll={updateImage}>
                    {entry[1]
                    .sort((a: any, b: any) =>
                      a.filename.split(".")?.[2]?.split("__")?.[1] < b.filename.split(".")?.[2]?.split("__")?.[1] ? -1 : 1)
                    .map((item: any, itemIndex: number) => (
                      <PreviewPic
                        key={itemIndex}
                        setState={props.setState}
                        selectedTab={selectedTab}
                        datasetId={props.datasetId}
                        previewItem={item}
                        initialLoad={initialLoad}
                        updateImage={updateImage}
                        imageUrl={imageUrl}
                        imageRef={imageRef}
                        index={item.originalIndex}
                        toUrl={toUrl}
                        urlPrefix={props.urlPrefix}
                      />
                    ))}
                  </div>
                </div>
              )})}
            </div>
            <div>
              {Object.entries(selectedDisplayItem?.twofilter || {})
              .sort((a: any, b: any) => a[0] < b[0] ? 1 : -1)
              .map((entry: any, entryIndex: number) => ([
                entry[0],
                entry[1]
                .filter((item: any) => Object.keys(item?.filter)?.length > 1)
                .filter((item: any) =>(
                  search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                  : true))
              ]))
              .filter((entry: any) => entry[1].length > 0)
              .sort((a: any, b: any) => {
                let aFilter: any = a[0].split(" by ");
                let bFilter: any = b[0].split(" by ");
                if (aFilter[0] < bFilter[0])
                  return 1
                else if (aFilter[0] > bFilter[0])
                  return -1
                else if (aFilter[1] < bFilter[1])
                  return -1
                else
                  return  1
              })
              .map((entry: any, entryIndex: number) => { return (
                <div key={entryIndex}>
                  <div>{entry[0]}</div>
                  <div style={{display: "flex", overflowX: "scroll"}} onScroll={updateImage}>
                    {entry[1]
                    .sort((a: any, b: any) =>
                      a.filename.split(".")?.[2] < b.filename.split(".")?.[2] ? -1 : 1)
                    .map((item: any, itemIndex: number) => { return(
                      <PreviewPic
                        key={itemIndex}
                        setState={props.setState}
                        selectedTab={selectedTab}
                        datasetId={props.datasetId}
                        previewItem={item}
                        initialLoad={initialLoad}
                        updateImage={updateImage}
                        imageUrl={imageUrl}
                        imageRef={imageRef}
                        index={item.originalIndex}
                        toUrl={toUrl}
                        urlPrefix={props.urlPrefix}
                      />
                    )})}
                  </div>
                </div>
              )})}
            </div>
          </>}
          {pane === 2 && //--------------------------------------Dimensions pane ---------------------------
          <>
            <div>
              {Object.entries(selectedDisplayItem?.twofilter || {})
              .sort((a: any, b: any) => a[0] < b[0] ? 1 : -1)
              .map((entry: any, entryIndex: number) => ([
                entry[0],
                entry[1]
                .filter((item: any) => (Object.keys(item?.filter)?.length === 1
                                        && !["y", "ym", "ymd"].includes(item?.parts?.[0]?.split("__")?.[0])))
                .filter((item: any) =>(
                  search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                  : true))
              ]))
              .filter((entry: any) => entry[1].length > 0)
              .map((entry: any, entryIndex: number) => { return (
                <div key={entryIndex}>
                  <div>{entry[0]}</div>
                  <div style={{display: "flex", overflowX: "scroll"}} onScroll={updateImage}>
                    {entry[1]
                    .sort((a: any, b: any) => {
                      return a.filename.split(".")?.[0] < b.filename.split(".")?.[0] ? -1 : 1
                    })
                    .map((item: any, itemIndex: number) => { return(
                      <PreviewPic
                        key={itemIndex}
                        setState={props.setState}
                        selectedTab={selectedTab}
                        datasetId={props.datasetId}
                        previewItem={item}
                        initialLoad={initialLoad}
                        updateImage={updateImage}
                        imageUrl={imageUrl}
                        imageRef={imageRef}
                        index={item.originalIndex}
                        toUrl={toUrl}
                        urlPrefix={props.urlPrefix}
                      />
                    )})}
                  </div>
                </div>
              )})}
            </div>
            <div style={{display: "flex", flexWrap: "wrap", marginTop: "10px"}}>
              {(selectedDisplayItem?.one || [])
              .filter((item: any) => !["y", "ym", "ymd"].includes(item?.parts?.[0]?.split("__")?.[0]))
              .filter((item: any) => (
                search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                : true
              ))
              .map((item: any, childIndex: number) => (
                <PreviewPic
                  key={childIndex}
                  setState={props.setState}
                  selectedTab={selectedTab}
                  datasetId={props.datasetId}
                  previewItem={item}
                  initialLoad={initialLoad}
                  updateImage={updateImage}
                  imageUrl={imageUrl}
                  imageRef={imageRef}
                  index={item.originalIndex}
                  toUrl={toUrl}
                  urlPrefix={props.urlPrefix}
                />
              ))}
            </div>
            <div style={{display: "flex", flexWrap: "wrap", marginTop: "10px"}}>
              {(selectedDisplayItem.two || [])
              .filter((item: any) => !["y", "ym", "ymd"].includes(item?.parts?.[0]?.split("__")?.[0]))
              .filter((item: any) => (
                search ? item.filename.toLowerCase().includes(search.toLocaleLowerCase())
                : true
              ))
              .map((item: any, childIndex: number) => (
                <PreviewPic
                  key={childIndex}
                  setState={props.setState}
                  selectedTab={selectedTab}
                  datasetId={props.datasetId}
                  previewItem={item}
                  initialLoad={initialLoad}
                  updateImage={updateImage}
                  imageUrl={imageUrl}
                  imageRef={imageRef}
                  index={item.originalIndex}
                  toUrl={toUrl}
                  urlPrefix={props.urlPrefix}
                />
              ))}
            </div>
          </>
          }
        </div>
        : !datasetId ?
        <div onClick={() => {
          props.onEvent({ message: "GetDatasetList", params: {}})
        }}>&lt;&lt;Choose Item to Display on the Left</div>
        : <></>}
        {displayPanes
          .map((label: string, original: number) => ({original, label}))
          .filter((item: {original: number, label: string}, index: number) => index > pane)
          .map((item: {original: number, label: string}, index: number) => (
            <div
              className={"cursor-pointer border border-gray-300 " + (pane === item.original ? "bg-green-200" : "bg-gray-200")} key={index}
              onClick={(e: any) => setPane(item.original)}>
              {item.label}
            </div>)
          )
        }
      </div>
    </div>
    <Dialog
      open={showChart}
      onOpenChange={(flag: boolean) => {
        if (!flag) {
          props.setState({dimList: [], filter: null, colorValues: null, showChart: false});
        }
      }}>
      <DialogContent
        className="h-9/10 w-9/10 !max-w-none flex flex-col overflow-auto"
        onOpenAutoFocus={(e) => e.preventDefault()}
        >
        <DialogTitle>Chart</DialogTitle>
        <Chart
          width={props.width}
          height={props.height}
          onEvent={props.onEvent}
          setState={props.setState}
          datasetId={props.datasetId}
          prefix={getPrefix(props.datasetId)}
          showChart={showChart}
          filename={props.filename}
          agg={props.agg}
          selectedTab={props.selectedTab}
          filter={props.filter}
          colorValues={props.colorValues}
          loggedin={props.loggedin}
          dimList={props.dimList}
          duckLoaded={props.duckLoaded}
          urlPrefix={props.urlPrefix}
          previewItems={previewItems}
        />
      </DialogContent>
    </Dialog>
    <Dialog
      open={openAdmin}
      onOpenChange={setOpenAdmin}
      >
      <DialogContent className="h-4/5 w-4/5 !max-w-none flex flex-col overflow-auto">
        <DialogTitle className="sr-only">Admin</DialogTitle>
        {  props.Admin && <Admin
          datasetId={datasetId}
          socketMessage={props.socketMessage}
          clearSocketMessage={props.clearSocketMessage}
        /> }
      </DialogContent>
    </Dialog>
  </>
  )
}

const PreviewPic = (props: any) => {
  let { previewItem, initialLoad, updateImage, selectedTab,
        imageUrl, imageRef, index, toUrl } = props;
  let agg = previewItem.filename?.split('.')?.[1];
  let filter = Object.entries(previewItem.filter || {})
    .map((entry: any) => `${entry[0]}__${entry[1]}`).join('__');

  let query = {
    datasetId: props.datasetId,
    dimList:  previewItem.filename?.split(".")?.[0]?.split("__"),
    agg: agg,
    selectedTab: selectedTab,
    filename: previewItem.filename,
    ...(filter ? {filter: filter} : {}),
    ...(previewItem.colorValues ? {colorValues: previewItem.colorValues} : {}),
  }

  return(
  <div
    style={{
      margin: "2px", width: "160px", height: "160px", cursor: "pointer",
      border: "solid grey 1px", borderRadius: "5px",
      padding: "2px"
    }}>
    <div
      onClick={(e: any) => {
        props.setState({...query, showChart: true});
      }}
      >
      <div
        style={{
          display: "flex", flexDirection: "column", height: "100%",
          justifyContent: "space-between", alignItems: "center", overflow: 'hidden'
        }}>
        <img
          onLoad={() => {
            if (!initialLoad.current) {
              initialLoad.current = true;
              updateImage();
            }
          }}
          ref={(el: any) => {
            imageRef.current[index] = el;
          }}
          style={{height: "90px", width: "auto"}}
          src={imageUrl.current?.[toUrl(previewItem.filename)]
               || `${props.urlPrefix}/blankscreen.png`}
        />
        <div style={{overflow: "hidden", fontSize: ".7rem"}}>
          <div style={{textAlign: "center"}}>
            {previewItem.filename?.split(".")?.[1]}
          </div>
          <div style={{textAlign: "center"}}>
            {previewItem.filename?.split(".")?.[0]?.split('__').join(", ")}
          </div>
          <div style={{textAlign: "center"}}>
            {previewItem.filename?.split(".")?.[2]?.split("__")
            .reduce((acc: string, cur: string, index: number) =>
                index % 2 === 1 ? acc + " " + cur : acc,
              "")}
          </div>
        </div>
      </div>
    </div>
  </div>)
}
